import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { RotateCcw, X, Facebook, MessageCircle, Share2, Send, Linkedin, Mail, Link2, Check, Download, Trophy, Loader2, Rocket } from "lucide-react";
import Confetti from "./Confetti";
import { XIcon } from "./SocialIcons";
import { getStoredRef } from "../lib/referral";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A stable per-browser id so a player can overwrite their own leaderboard
// score, while their chosen name stays reserved to them.
const getVisitorId = () => {
  let id = localStorage.getItem("sharkbowl_visitor_id");
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("sharkbowl_visitor_id", id);
  }
  return id;
};

// A quick, delightful bubble-pop mini-game with a countdown race.
// Bubbles float up continuously for GAME_DURATION; click/tap to pop them.
// Best score is remembered in localStorage; a celebratory banner with
// confetti and a share button appears when time runs out.
const COLORS = [
  "radial-gradient(circle at 30% 30%, #ffffff, #4fc9dc)",
  "radial-gradient(circle at 30% 30%, #ffffff, #fbbf24)",
  "radial-gradient(circle at 30% 30%, #ffffff, #fb7185)",
  "radial-gradient(circle at 30% 30%, #ffffff, #34d399)",
  "radial-gradient(circle at 30% 30%, #ffffff, #a78bfa)",
];
const GAME_DURATION = 12000; // ms

export default function BubblePopGame({ active, onClose }) {
  const [bubbles, setBubbles] = useState([]);
  const [score, setScore] = useState(0);
  const [banner, setBanner] = useState(false);
  const [best, setBest] = useState(0);
  const [beatBest, setBeatBest] = useState(false);
  const [progress, setProgress] = useState(100); // timer bar width %
  const [copied, setCopied] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);
  const [playerName, setPlayerName] = useState(() => localStorage.getItem("sb-player-name") || "");
  const [board, setBoard] = useState([]);
  const [rank, setRank] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [nameError, setNameError] = useState("");
  const [nlEmail, setNlEmail] = useState("");
  const [nlLoading, setNlLoading] = useState(false);
  const [nlDone, setNlDone] = useState(false);
  const [round, setRound] = useState(0); // bump to replay
  const timers = useRef([]);
  const intervals = useRef([]);
  const seed = useRef(0);
  const scoreRef = useRef(0);

  useEffect(() => {
    const stored = parseInt(localStorage.getItem("sb-bubble-best") || "0", 10);
    setBest(isNaN(stored) ? 0 : stored);
  }, []);

  // Lock the page (no scroll / no background taps) while the game is on.
  useEffect(() => {
    if (!active) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouch = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouch;
    };
  }, [active]);

  const clearAll = () => {
    timers.current.forEach(clearTimeout);
    intervals.current.forEach(clearInterval);
    timers.current = [];
    intervals.current = [];
  };

  useEffect(() => {
    if (!active) return;
    setScore(0);
    scoreRef.current = 0;
    setBanner(false);
    setBeatBest(false);
    setCopied(false);
    setProgress(100);
    setBubbles([]);

    const start = Date.now();

    // continuous bubble spawning
    const spawn = () => {
      const id = seed.current++;
      setBubbles((prev) => [
        ...prev.slice(-30),
        {
          id,
          left: 4 + Math.random() * 90,
          size: 30 + Math.random() * 46,
          dur: 3.8 + Math.random() * 3,
          color: COLORS[id % COLORS.length],
          drift: (Math.random() - 0.5) * 130,
        },
      ]);
    };
    for (let i = 0; i < 6; i++) timers.current.push(setTimeout(spawn, i * 120));
    intervals.current.push(setInterval(spawn, 360));

    // timer bar
    intervals.current.push(
      setInterval(() => {
        const pct = Math.max(0, 100 - ((Date.now() - start) / GAME_DURATION) * 100);
        setProgress(pct);
      }, 60)
    );

    // end of round
    timers.current.push(
      setTimeout(() => {
        clearAll();
        setBubbles([]);
        const finalScore = scoreRef.current;
        const prevBest = parseInt(localStorage.getItem("sb-bubble-best") || "0", 10) || 0;
        if (finalScore > prevBest) {
          localStorage.setItem("sb-bubble-best", String(finalScore));
          setBest(finalScore);
          setBeatBest(finalScore > 0);
        } else {
          setBest(prevBest);
        }
        setBanner(true);
        makeCard(finalScore, Math.max(finalScore, prevBest));
      }, GAME_DURATION)
    );

    return clearAll;
  }, [active, round]);

  // Draw a fun, shareable score card onto a canvas -> data URL
  const makeCard = (sc, bst) => {
    const W = 1200, H = 630;
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");

    // ocean gradient bg
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#0f766e");
    g.addColorStop(0.5, "#0891b2");
    g.addColorStop(1, "#0e7490");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    // decorative bubbles
    const cols = ["rgba(255,255,255,0.18)", "rgba(255,255,255,0.10)", "rgba(125,211,252,0.25)"];
    for (let i = 0; i < 26; i++) {
      ctx.beginPath();
      const r = 8 + Math.random() * 46;
      ctx.fillStyle = cols[i % cols.length];
      ctx.arc(Math.random() * W, Math.random() * H, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // All text is drawn here, AFTER the logo, so nothing overlaps
    const drawText = () => {
      ctx.textAlign = "center";

      // header (below the logo)
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.font = "800 44px Poppins, sans-serif";
      ctx.fillText("SHARKBOWL GAMES", W / 2, 218);
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.font = "600 26px Inter, sans-serif";
      ctx.fillText("BUBBLE POP CHALLENGE", W / 2, 256);

      // big score
      ctx.fillStyle = "#ffffff";
      ctx.font = "800 180px Poppins, sans-serif";
      ctx.fillText(String(sc), W / 2, 430);
      ctx.font = "700 40px Poppins, sans-serif";
      ctx.fillText("BUBBLES POPPED", W / 2, 480);

      // best pill
      ctx.fillStyle = "rgba(15,23,42,0.35)";
      const pillW = 320, pillH = 58, px = (W - pillW) / 2, py = 508;
      roundRect(ctx, px, py, pillW, pillH, 29);
      ctx.fill();
      ctx.fillStyle = "#fde68a";
      ctx.font = "700 30px Poppins, sans-serif";
      ctx.fillText(`\u{1F3C6} Personal best: ${bst}`, W / 2, py + 39);

      // footer
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.font = "600 24px Inter, sans-serif";
      ctx.fillText("Can you beat me?  \u2022  sharkbowlgames.com", W / 2, 602);

      try { setCardUrl(canvas.toDataURL("image/png")); } catch (e) { /* ignore */ }
    };

    // draw the real logo (white) at the top, then the text
    const logo = new Image();
    logo.onload = () => {
      const size = 120;
      const off = document.createElement("canvas");
      off.width = size; off.height = size;
      const octx = off.getContext("2d");
      octx.drawImage(logo, 0, 0, size, size);
      octx.globalCompositeOperation = "source-in";
      octx.fillStyle = "#ffffff";
      octx.fillRect(0, 0, size, size);
      ctx.drawImage(off, W / 2 - size / 2, 44, size, size);
      drawText();
    };
    logo.onerror = drawText;
    logo.src = "/sharkbowl-logo.png";
  };

  const downloadCard = () => {
    if (!cardUrl) return;
    const a = document.createElement("a");
    a.href = cardUrl;
    a.download = "sharkbowl-score.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const loadBoard = () => {
    axios.get(`${API}/leaderboard`, { timeout: 15000 })
      .then((res) => { if (Array.isArray(res.data)) setBoard(res.data); })
      .catch(() => {});
  };

  useEffect(() => { loadBoard(); }, []);

  const submitScore = async () => {    const name = (playerName || "Anon").trim().slice(0, 20);
    localStorage.setItem("sb-player-name", name);
    setNameError("");
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/leaderboard`,
        { name, score, clientId: getVisitorId() },
        { timeout: 15000 }
      );
      if (res.data && res.data.ok === false) {
        setNameError(res.data.message || "That name is already taken. Try another!");
        return;
      }
      setRank(res.data && res.data.rank ? res.data.rank : null);
      setSubmitted(true);
      loadBoard();
    } catch (e) {
      setNameError("Couldn't submit your score. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!nlEmail.includes("@")) return;
    setNlLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email: nlEmail.trim(), source: "game-over", ref: getStoredRef() }, { timeout: 15000 });
      setNlDone(true);
      trackEvent("sign_up", { method: "game-over" });
      setNlEmail("");
    } catch (err) {
      /* ignore */
    } finally {
      setNlLoading(false);
    }
  };

  const pop = (id) => {
    setBubbles((prev) => prev.filter((b) => b.id !== id));
    scoreRef.current += 1;
    setScore(scoreRef.current);
  };

  const playAgain = () => {
    setBanner(false);
    setShowShare(false);
    setCardUrl(null);
    setSubmitted(false);
    setRank(null);
    setNameError("");
    setNlDone(false);
    setNlEmail("");
    setRound((r) => r + 1);
  };

  const close = () => {
    clearAll();
    setBanner(false);
    setShowShare(false);
    setSubmitted(false);
    setRank(null);
    setNameError("");
    setBubbles([]);
    onClose && onClose();
  };

  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const shareText = `I popped ${score} bubbles${score >= best && score > 0 ? " (a new personal best!)" : ""} playing with Finn the shark at Sharkbowl Games! 🦈 Think you can beat me?`;

  const shareTo = (network) => {
    const t = encodeURIComponent(shareText);
    const u = encodeURIComponent(shareUrl);
    const tu = encodeURIComponent(`${shareText} ${shareUrl}`);
    let link = "";
    if (network === "x") link = `https://twitter.com/intent/tweet?text=${t}&url=${u}`;
    else if (network === "facebook") link = `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`;
    else if (network === "whatsapp") link = `https://wa.me/?text=${tu}`;
    else if (network === "reddit") link = `https://www.reddit.com/submit?url=${u}&title=${t}`;
    else if (network === "telegram") link = `https://t.me/share/url?url=${u}&text=${t}`;
    else if (network === "linkedin") link = `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    else if (network === "email") link = `mailto:?subject=${encodeURIComponent("My Sharkbowl bubble score!")}&body=${tu}`;
    if (link) window.open(link, "_blank", "noopener,noreferrer,width=600,height=520");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* ignore */
    }
  };

  const RedditIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0Zm5.01 4.74c.69 0 1.25.56 1.25 1.25s-.56 1.25-1.25 1.25c-.68 0-1.23-.54-1.25-1.21l-2.05-.43-.63 2.97c1.43.06 2.72.5 3.66 1.16.24-.24.57-.39.94-.39.74 0 1.34.6 1.34 1.34 0 .53-.31.99-.77 1.21a2.7 2.7 0 0 1 .03.42c0 2.14-2.49 3.87-5.56 3.87-3.08 0-5.56-1.73-5.56-3.87 0-.14 0-.29.03-.43-.44-.23-.74-.68-.74-1.2 0-.74.6-1.34 1.34-1.34.36 0 .69.14.93.38.94-.66 2.24-1.1 3.68-1.16l.7-3.31c.02-.08.06-.14.12-.18.07-.04.14-.05.22-.04l2.31.49c.17-.31.5-.53.88-.53Zm-6.86 5.9c-.69 0-1.25.56-1.25 1.25s.56 1.25 1.25 1.25 1.25-.56 1.25-1.25-.56-1.25-1.25-1.25Zm3.72 0c-.69 0-1.25.56-1.25 1.25s.56 1.25 1.25 1.25 1.25-.56 1.25-1.25-.56-1.25-1.25-1.25Zm-3.75 3.66a.34.34 0 0 0-.24.58c.55.55 1.6.6 1.91.6.31 0 1.36-.05 1.91-.6a.35.35 0 0 0 .09-.24.35.35 0 0 0-.35-.34.34.34 0 0 0-.23.09c-.35.34-1.09.47-1.42.47-.33 0-1.08-.13-1.42-.47a.34.34 0 0 0-.24-.09Z" />
    </svg>
  );
  const TelegramIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M11.94 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0h-.06Zm5.03 8.16c-.13 1.39-.7 4.77-1 6.33-.12.66-.36.88-.6.9-.5.05-.9-.33-1.38-.65-.76-.5-1.19-.81-1.93-1.3-.85-.56-.3-.87.19-1.37.13-.13 2.32-2.13 2.36-2.31.01-.02.01-.11-.04-.15s-.13-.03-.19-.02c-.08.02-1.38.88-3.89 2.58-.37.25-.7.38-1 .37-.33-.01-.96-.19-1.43-.34-.58-.19-1.04-.29-1-.61.02-.17.25-.34.69-.52 2.72-1.18 4.53-1.96 5.43-2.35 2.58-1.07 3.12-1.26 3.47-1.26.08 0 .25.02.36.11.09.07.12.17.13.24.01.06.03.19.02.29Z" />
    </svg>
  );

  if (!active) return null;

  return (
    <div className="fixed inset-0 z-[85] overflow-hidden">
      {/* Click-blocking backdrop so page buttons aren't clickable during play */}
      <div
        className="absolute inset-0 pointer-events-auto"
        style={{ background: banner ? "rgba(2,6,23,0.55)" : "transparent" }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Score + timer HUD */}
      {!banner && (
        <div className="absolute top-24 left-1/2 -translate-x-1/2 pointer-events-none w-[260px] max-w-[80vw]">
          <div className="flex items-center justify-between gap-2 bg-white/95 dark:bg-slate-800/95 backdrop-blur px-5 py-2 rounded-t-2xl font-display font-800 text-teal-600 dark:text-cyan-300 shadow-lg border-2 border-b-0 border-teal-200 dark:border-cyan-500/30">
            <span>Popped: {score}</span>
            {best > 0 && <span className="text-orange-500 dark:text-orange-300 text-sm">Best {best}</span>}
          </div>
          <div className="h-3 bg-white/95 dark:bg-slate-800/95 border-2 border-t-0 border-teal-200 dark:border-cyan-500/30 rounded-b-2xl overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-400 to-cyan-400 transition-[width] duration-75 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {bubbles.map((b) => (
        <button
          key={b.id}
          onPointerDown={() => pop(b.id)}
          className="game-bubble pointer-events-auto"
          style={{
            left: `${b.left}%`,
            width: b.size,
            height: b.size,
            background: b.color,
            animationDuration: `${b.dur}s`,
            touchAction: "none",
            "--gdrift": `${b.drift}px`,
          }}
          aria-label="Pop bubble"
        />
      ))}

      {banner && (
        <>
          <Confetti count={beatBest ? 130 : 80} />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-4">
            <div data-testid="game-over-banner" className="pointer-events-auto relative bg-white dark:bg-slate-800 rounded-[2rem] px-5 sm:px-10 py-8 text-center shadow-2xl border-4 border-teal-300 dark:border-cyan-500/40 -rotate-1 max-w-sm w-full max-h-[88vh] overflow-y-auto">
              <button
                onClick={close}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                aria-label="Close game"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="font-display font-800 text-3xl text-slate-900 dark:text-white">
                {beatBest ? "New best! 🏆" : "Splash-tastic!"}
              </div>
              <div className="mt-2 text-slate-500 dark:text-slate-300 font-semibold">
                {beatBest
                  ? `Wow, ${score} bubbles is your best yet!`
                  : `You popped ${score} bubble${score === 1 ? "" : "s"} with Finn.`}
              </div>
              {!beatBest && best > 0 && score < best && (
                <div className="mt-1 text-sm text-slate-400 dark:text-slate-400">
                  Your best is {best}, pop {best - score + 1} more to beat it!
                </div>
              )}

              {/* Leaderboard */}
              <div className="mt-6 text-left">
                {!submitted ? (
                  <div className="rounded-2xl bg-teal-50 dark:bg-white/5 border border-teal-100 dark:border-white/10 p-4">
                    <p className="flex items-center gap-2 font-display font-700 text-slate-800 dark:text-white justify-center mb-3">
                      <Trophy className="w-5 h-5 text-amber-500" /> Join the leaderboard
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        value={playerName}
                        onChange={(e) => { setPlayerName(e.target.value); if (nameError) setNameError(""); }}
                        placeholder="Your name"
                        maxLength={20}
                        data-testid="leaderboard-name-input"
                        className="w-full sm:flex-1 h-11 px-4 rounded-full bg-white dark:bg-slate-900 border border-teal-200 dark:border-white/15 text-slate-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                      <button
                        onClick={submitScore}
                        disabled={submitting}
                        data-testid="leaderboard-submit-button"
                        className="h-11 px-5 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm w-full sm:w-auto shrink-0 flex items-center justify-center gap-1.5 disabled:opacity-60"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />} Submit
                      </button>
                    </div>
                    {nameError && (
                      <p data-testid="leaderboard-name-error" className="mt-2 text-center text-sm font-semibold text-rose-500 dark:text-rose-400">
                        {nameError}
                      </p>
                    )}
                  </div>
                ) : (
                  rank && (
                    <div className="rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-4 text-center">
                      <p className="font-display font-800 text-lg">
                        {rank === 1 ? "You're #1! 👑" : `You ranked #${rank}!`}
                      </p>
                      <p className="text-white/80 text-sm">with {score} bubbles popped</p>
                    </div>
                  )
                )}

                {board.length > 0 && (
                  <div className="mt-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/10 overflow-hidden">
                    <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 px-4 pt-3">
                      <Trophy className="w-3.5 h-3.5 text-amber-500" /> Top players
                    </p>
                    <ul className="p-2">
                      {board.map((row, idx) => (
                        <li
                          key={idx}
                          className={`flex items-center justify-between px-3 py-1.5 rounded-lg text-sm ${idx === 0 ? "bg-amber-50 dark:bg-amber-500/10" : ""}`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <span className={`w-5 text-center font-800 font-display ${idx === 0 ? "text-amber-500" : idx === 1 ? "text-slate-400" : idx === 2 ? "text-orange-400" : "text-slate-300 dark:text-slate-500"}`}>
                              {idx + 1}
                            </span>
                            <span className="font-semibold text-slate-700 dark:text-slate-200 truncate">{row.name}</span>
                          </span>
                          <span className="font-800 font-display text-teal-600 dark:text-cyan-300">{row.score}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Early-access email capture — highest-intent moment */}
              <div className="mt-5">
                {nlDone ? (
                  <div data-testid="game-newsletter-done" className="rounded-2xl bg-teal-50 dark:bg-white/5 border border-teal-100 dark:border-white/10 p-4 text-center">
                    <p className="font-display font-700 text-slate-800 dark:text-white flex items-center justify-center gap-2">
                      <Check className="w-5 h-5 text-teal-500" /> You're on the list! 🎁
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      Check your inbox to confirm — your free Finn wallpaper pack lands right after.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-white/5 dark:to-white/5 border border-teal-100 dark:border-white/10 p-4 text-left">
                    <p className="flex items-center gap-2 font-display font-700 text-slate-800 dark:text-white justify-center mb-1">
                      <Rocket className="w-5 h-5 text-teal-500" /> Get early access to our next game
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 text-center mb-3">Plus a free Finn wallpaper pack — no spam, ever.</p>
                    <form onSubmit={submitEmail} className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="email"
                        value={nlEmail}
                        onChange={(e) => setNlEmail(e.target.value)}
                        placeholder="you@email.com"
                        data-testid="game-newsletter-input"
                        className="w-full sm:flex-1 h-11 px-4 rounded-full bg-white dark:bg-slate-900 border border-teal-200 dark:border-white/15 text-slate-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                      <button
                        type="submit"
                        disabled={nlLoading}
                        data-testid="game-newsletter-submit"
                        className="h-11 px-5 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm w-full sm:w-auto shrink-0 flex items-center justify-center gap-1.5 disabled:opacity-60"
                      >
                        {nlLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Notify me
                      </button>
                    </form>
                  </div>
                )}
              </div>


              {cardUrl && (
                <div className="mt-5">
                  <img
                    src={cardUrl}
                    alt="Your score card"
                    className="w-full rounded-xl border-2 border-teal-200 dark:border-cyan-500/30 shadow-md"
                  />
                  <button
                    onClick={downloadCard}
                    className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-teal-700 dark:text-cyan-300 hover:underline"
                  >
                    <Download className="w-4 h-4" /> Save score card
                  </button>
                </div>
              )}

              <div className="mt-6">
                {!showShare ? (
                  <button
                    onClick={() => setShowShare(true)}
                    className="inline-flex items-center gap-2 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-bold px-6 py-2.5 transition-colors hover:-translate-y-0.5"
                  >
                    <Share2 className="w-4 h-4" /> Share your score
                  </button>
                ) : (
                  <div className="animate-[fadeIn_0.2s_ease]">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Share to</p>
                    <div className="grid grid-cols-4 gap-3 max-w-[280px] mx-auto">
                      <ShareBtn onClick={() => shareTo("x")} label="X" bg="bg-slate-900"><XIcon className="w-5 h-5" /></ShareBtn>
                      <ShareBtn onClick={() => shareTo("facebook")} label="Facebook" bg="bg-[#1877f2]"><Facebook className="w-5 h-5" fill="currentColor" /></ShareBtn>
                      <ShareBtn onClick={() => shareTo("reddit")} label="Reddit" bg="bg-[#ff4500]"><RedditIcon className="w-5 h-5" /></ShareBtn>
                      <ShareBtn onClick={() => shareTo("whatsapp")} label="WhatsApp" bg="bg-[#25d366]"><MessageCircle className="w-5 h-5" fill="currentColor" /></ShareBtn>
                      <ShareBtn onClick={() => shareTo("telegram")} label="Telegram" bg="bg-[#229ed9]"><TelegramIcon className="w-5 h-5" /></ShareBtn>
                      <ShareBtn onClick={() => shareTo("linkedin")} label="LinkedIn" bg="bg-[#0a66c2]"><Linkedin className="w-5 h-5" fill="currentColor" /></ShareBtn>
                      <ShareBtn onClick={() => shareTo("email")} label="Email" bg="bg-slate-500"><Mail className="w-5 h-5" /></ShareBtn>
                      <ShareBtn onClick={copyLink} label={copied ? "Copied!" : "Copy link"} bg={copied ? "bg-green-500" : "bg-teal-600"}>
                        {copied ? <Check className="w-5 h-5" /> : <Link2 className="w-5 h-5" />}
                      </ShareBtn>
                    </div>
                  </div>
                )}
                <button
                  onClick={playAgain}
                  className="mt-5 inline-flex items-center gap-2 rounded-full border-2 border-teal-500 text-teal-700 dark:text-cyan-300 dark:border-cyan-400/50 font-bold px-6 py-2.5 hover:bg-teal-50 dark:hover:bg-white/5 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" /> Play again
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ShareBtn({ onClick, label, bg, children }) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 group"
      aria-label={`Share via ${label}`}
      title={label}
    >
      <span className={`w-12 h-12 rounded-2xl ${bg} text-white flex items-center justify-center group-hover:scale-110 group-hover:-rotate-3 transition-transform shadow-md`}>
        {children}
      </span>
      <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 truncate max-w-[60px]">{label}</span>
    </button>
  );
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
